import { useMemo } from 'react'

import { ResourceCategory } from '@/features/activationGroup/types'
import { ALL_RESULTS } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { GET_BID_HISTORIES_API_ID } from '@/features/bidding/endpoints/bids'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistoriesPage } from '@/features/bidding/types/bidHistory'
import type { BidOfferPriceCommonData } from '@/features/bidding/types/bidOfferPriceFlow'
import getBidConfig from '@/features/bidding/utils/getBidConfig'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { mapActivationGroupBidsToAggregatedBids } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { filterBidsByEligibleActivationGroups } from '@/features/bidding/utils/price/filterBidsByEligibleActivationGroups'

import { useBidHistoriesPageQuery } from './useBidHistoriesPageQuery'
import { useBidOfferDerivedData } from './useBidOfferDerivedData'
import { useBidOfferEligibleActivationGroups } from './useBidOfferEligibleActivationGroups'
import { useBidOfferPriceBidsWithPrices } from './useBidOfferPriceBidsWithPrices'
import { useBidOfferPriceDataRequirements } from './useBidOfferPriceDataRequirements'

export type BidOfferPriceData_ByActivationGroup = {
  common: BidOfferPriceCommonData
  bidData: {
    bidsWithPrices: ExtendedBid[]
    initialBidsPerActivationGroup: Map<string, AggregatedBidWrapper>
    bidHistoriesPage: BidHistoriesPage | undefined
    latestNonDraftBidsByActivationGroup: Map<string, ExtendedBid | null>
    latestAcceptedBidsByActivationGroup: Map<string, ExtendedBid | null>
  }
}

export type UseBidOfferPriceDataByActivationGroupReturn =
  | { isLoading: true; data: null }
  | { isLoading: false; data: BidOfferPriceData_ByActivationGroup }

/**
 * Hook that fetches and computes data for the by-activation-group price flow.
 *
 * This hook is used when prices are managed separately per activation group
 * (i.e., when requiresPriceByActivationGroup is true, typically for MFRR_UP/DOWN).
 *
 * @param groupParams - Grouped bid parameters (already loaded by parent)
 * @returns Data needed for the by-activation-group price flow, including common data and per-group bids
 */
export function useBidOfferPriceData_ByActivationGroup(
  groupParams: GroupedBidParams,
): UseBidOfferPriceDataByActivationGroupReturn {
  const { selectedCountry } = useBiddingContext()

  // 1. Determine data requirements
  const requirements = useBidOfferPriceDataRequirements(groupParams)

  // 2. Fetch bids with per-activation-group prices
  const { bidsWithPrices, isLoading: isLoadingBids } = useBidOfferPriceBidsWithPrices({
    groupParams,
    requiresPriceByActivationGroup: true,
  })

  // 3. Fetch eligible activation groups matching the groupParams
  const { eligibleActivationGroups: rawEligibleActivationGroups, isLoading: isLoadingActivationGroups } =
    useBidOfferEligibleActivationGroups(groupParams)

  // 3a. Filter activation groups to only C_AND_I resource category if required
  const eligibleActivationGroups = requirements.requiresOnlyCAndIActivationGroups
    ? rawEligibleActivationGroups?.filter((ag) => ag.resourceCategory === ResourceCategory.C_AND_I)
    : rawEligibleActivationGroups

  // 3b. Filter bids to only include those matching eligible activation groups
  // This ensures that bids for activation groups not in the current flow
  // (e.g., BESS groups when only handling C_AND_I) are excluded to maintain
  // consistency between bids and eligible activation groups
  const filteredBidsWithPrices = useMemo(
    () => filterBidsByEligibleActivationGroups(bidsWithPrices, eligibleActivationGroups),
    [bidsWithPrices, eligibleActivationGroups],
  )

  // 4. Fetch bid histories page for the group params
  // We memoize the params and use a custom query key with serialized MarketDate to avoid
  // infinite loops caused by MarketDate object reference instability in TanStack Query's key comparison
  const bidHistoriesParams = useMemo(
    () => ({
      pagination: { pageSize: ALL_RESULTS, page: 0 },
      sorting: [],
      filter: {
        items: [
          { field: 'portfolio', operator: 'equals', value: groupParams.portfolioCode },
          { field: 'marketProgram', operator: 'equals', value: groupParams.marketProgram },
          { field: 'deliveryDay', operator: 'equals', value: groupParams.deliveryDay },
          { field: 'bidType', operator: 'equals', value: groupParams.bidType },
        ],
      },
      countryFilter: selectedCountry,
    }),
    [
      groupParams.portfolioCode,
      groupParams.marketProgram,
      groupParams.deliveryDay.toISODate(),
      groupParams.bidType,
      selectedCountry,
    ],
  )

  const bidHistoriesQueryKey = useMemo(
    () =>
      `${GET_BID_HISTORIES_API_ID}_${groupParams.portfolioCode}_${groupParams.marketProgram}_${groupParams.deliveryDay.toISODate()}_${groupParams.bidType}_${selectedCountry}`,
    [
      groupParams.portfolioCode,
      groupParams.marketProgram,
      groupParams.deliveryDay.toISODate(),
      groupParams.bidType,
      selectedCountry,
    ],
  )

  const { bidHistoriesPage, isFetching: isFetchingBidHistories } = useBidHistoriesPageQuery(bidHistoriesParams, {
    queryKey: bidHistoriesQueryKey,
  })

  // 5. Get latest non-draft and latest accepted bids from bid histories
  const { latestNonDraftBidsByActivationGroup, latestAcceptedBidsByActivationGroup } = useBidOfferDerivedData({
    bidHistories: bidHistoriesPage?.bidHistories,
  })

  // 6. Compute initial bids per activation group
  const initialBidsPerActivationGroup = useMemo(() => {
    const { priceLadderType } = getBidConfig(selectedCountry, groupParams.bidType)
    return mapActivationGroupBidsToAggregatedBids(
      filteredBidsWithPrices,
      eligibleActivationGroups ?? [],
      priceLadderType,
      groupParams,
    )
  }, [selectedCountry, groupParams.bidType, filteredBidsWithPrices, eligibleActivationGroups])

  // Check if still loading
  if (isLoadingBids || isLoadingActivationGroups || isFetchingBidHistories) {
    return { isLoading: true, data: null }
  }

  // Return the final data object
  // Note: We don't memoize because bidHistoriesPage from TanStack Query creates new object references
  // on every render, which would make memoization ineffective and cause infinite re-render loops
  return {
    isLoading: false,
    data: {
      common: {
        eligibleActivationGroups: eligibleActivationGroups ?? [],
      },
      bidData: {
        bidsWithPrices: filteredBidsWithPrices,
        initialBidsPerActivationGroup,
        bidHistoriesPage,
        latestNonDraftBidsByActivationGroup,
        latestAcceptedBidsByActivationGroup,
      },
    },
  }
}
